import type { HTMLComponent } from "@commercekit/react";
import { useNavigate } from "@remix-run/react";
import { useLocationSearch } from "@commercekit/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

const WvProductsSortingCard: HTMLComponent = () => {
  const { $pt } = usePrismicLabels();
  const navigate = useNavigate();
  const { linkTo, search: searchParams } = useLocationSearch();

  const sortQuery = useMemo(() => {
    const sortDirection = searchParams.get("sort_direction")?.toUpperCase() || "";
    const sortName = searchParams.get("sort_name")?.toUpperCase() || "";

    return sortDirection && sortName ? `${sortDirection}_${sortName}` : "";
  }, [searchParams]);

  const [sortMethod, setSortMethod] = useState(sortQuery);

  const updateSortQuery = useCallback(
    (value: string) => {
      // WOV-2195: a new sort is a new result set, so drop ?page=N.
      // Early return if the value is not changing.
      if (value.toUpperCase() === sortQuery) return;

      // Navigate to the default state if the value is empty.
      if (!value) {
        navigate(linkTo({ sort_direction: "", sort_name: "", page: "" }));
        return;
      }

      const [sortDirection, sortName] = value.split("_").map(part => part.toUpperCase());
      navigate(linkTo({ sort_direction: sortDirection, sort_name: sortName, page: "" }));
    },
    [sortQuery, navigate, linkTo]
  );

  useEffect(() => {
    updateSortQuery(sortMethod);
  }, [sortMethod, updateSortQuery]);

  return (
    <div className="d-flex justify-content-end">
      <div className="d-flex col-lg-auto d-lg-flex mx-lg-n3 align-items-center justify-content-end">
        <div className="sorting_input_wrapper col-auto d-lg-flex align-items-center">
          <select
            className="form-control"
            onChange={(e) => setSortMethod(() => e?.target?.value)}
            defaultValue={sortQuery}
          >
            <option value="">{$pt("sort_products_default_placeholder")}</option>
            <option value="ASCENDING_PRICE">
              {$pt("sort_products_by_price_increasing")}
            </option>
            <option value="DESCENDING_PRICE">
              {$pt("sort_products_by_price_decreasing")}
            </option>
            <option value="ASCENDING_NAME">
              {$pt("sort_products_by_name_increasing")}
            </option>
            <option value="DESCENDING_NAME">
              {$pt("sort_products_by_name_decreasing")}
            </option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default WvProductsSortingCard;
